import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import * as _ from 'lodash';
import * as moment from 'moment';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, switchMap, tap } from 'rxjs';
import { DURATION_OPTIONS, FREQUENCY_OPTIONS, MEDICATION_ICONS, PRESCRIPTION_INSTRUCTIONS, PRN_INSTRUCTIONS, ROUTE_OPTIONS } from './../../../core/data/app.data';
import { FeedbackService } from './../../../core/services/feedback.service';
import { GroupService } from './../../../core/services/group.service';
import { MedicationService } from './../../../core/services/medication.service';
import { PatientPrescriptionService } from './../../../core/services/patient-prescription.service';
import { PatientService } from './../../../core/services/patient.service';
import { UserService } from './../../../core/services/user.service';

@Component({
  selector: 'app-medication-autocomplete',
  templateUrl: './medication-autocomplete.component.html',
  styleUrls: ['./medication-autocomplete.component.scss']
})
export class MedicationAutocompleteComponent {

  @Input() initialData: any[] = [];
  @Input() showActions: boolean = true;
  @Input() patient: any;
  @Output() changed = new EventEmitter<any[]>();
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('authorizeSigningDialog') authorizeSigningDialog?: TemplateRef<any>;

  groupUsers: any[] = [];

  medicationControl = new FormControl();
  medicationBundleControl = new FormControl();
  patientControl = new FormControl();
  filteredData: Observable<any[]>;
  existingData = new Set<string>();
  medicationList: any[] = [];
  readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
  addOnBlur = true;


  frequencyOptions: any[] = FREQUENCY_OPTIONS;
  durationOptions: any[] = DURATION_OPTIONS;
  instructionSuggestions: any[] = PRESCRIPTION_INSTRUCTIONS;
  prnInstructions: any[] = PRN_INSTRUCTIONS;

  confirmSubstitutions: boolean = false;
  signingAuthPassword: string = '';
  signingAuthPhysician: string = '';
  forceSignature: boolean = true;

  /**
   * True when the signed-in user is a physician, who may only ever sign as themselves.
   *
   * Read directly off the profile rather than through `userService.isUserPhysician`, which
   * dereferences `this.user` unguarded and would throw during change detection before the
   * profile has loaded.
   */
  get isPhysicianUser(): boolean {
    return this.userService.user?.userType === 'doctor';
  }

  /** The signed-in user's own display name, shown in place of the picker for a physician. */
  get currentUserDisplayName(): string {
    const user = this.userService.user;
    if (!user) return '';
    return `${user.title || ''} ${user.name || ''} ${user.surname || ''}`.trim();
  }
  email: string = '';
  ccEmail: string = '';


  public expandedHeader: string = 'active';
  activeMedication!: any[] | null;


  medicationBundles: any[] = [];

  searchCompleted: boolean = false;

  constructor(
    private userService: UserService,
    private groupService: GroupService,
    private medicationService: MedicationService,
    private feedbackService: FeedbackService,
    private patientService: PatientService,
    private patientPrescriptionService: PatientPrescriptionService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.filteredData = this.medicationControl.valueChanges.pipe(
      debounceTime(500), // Adjust the debounce time as needed (in milliseconds)
      distinctUntilChanged(),
      switchMap(value => this.filterMedication(value))
    );

    this.groupService.groupUsers$
      .subscribe((users: any[]) => {
        if (users?.length) {
          this.groupUsers = users?.filter(u => u.userType === 'doctor');
          const currentUser = this.userService.user;
          this.email = currentUser.email;
          if (this.isPhysicianUser) {
            // A physician always signs as THEMSELVES. Delegated signing is an administrative
            // function — `canSignOnDoctorsBehalf` is for admin staff and does not extend to
            // doctors. Set here as well as hidden in the template so the value cannot be reached
            // by any other route, and so it is correct even if groupUsers has not arrived yet.
            //
            // The server enforces the same rule (resolvePrescriptionAuthority): a physician
            // naming a colleague has the signature withheld. Without this the UI let them pick a
            // colleague, accepted that colleague's password, and then silently produced an
            // unsigned script — a dead end with no explanation.
            this.signingAuthPhysician = currentUser.id;
          } else if (this.groupUsers.findIndex(gu => gu.userId === currentUser.id) > -1) {
            this.signingAuthPhysician = currentUser.id;
          } else {
            // Preselect a doctor for admin staff to sign on behalf of. Guarded: a group with no
            // eligible doctor previously threw on [0].userId and took the whole subscription with it.
            const fallback = this.groupUsers.filter(u => u.userRole === 'Admin' && u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50')[0];
            this.signingAuthPhysician = fallback ? fallback.userId : '';
          }
        }
      });

    this.patientPrescriptionService.patientMedication$
      .pipe(
        map((medication) => {
          return medication?.filter(((i: any) => i.active)) || [];
        }),
      ).subscribe({
        next: (medication) => {
          this.activeMedication = medication;
        },
        error: (err) => {
          this.activeMedication = [];
        }
      });

    this.medicationService.bundles$
      .subscribe(bundles => {
        if (bundles) {
          this.medicationBundles = bundles;
        }
      });
  }

  ngOnInit(): void {
    /* if (this.initialData) {
      this.bindMedicationList(this.initialData);
    } */
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData']) {
      this.bindMedicationList(changes['initialData'].currentValue);
    }
  }

  toggleHeader(item: string) {
    this.expandedHeader = this.expandedHeader === item ? '' : item;
  }

  selectMedicationBundle(event: MatSelectChange) {
    const bundle = event.value;
    if (!bundle) return;
    this.medicationList = [];
    this.existingData.clear();
    this.bindMedicationList(bundle.medication);
    this.medicationBundleControl.setValue('');
    this.confirmSubstitutions = bundle.confirmSubstitutions;
  }

  bindMedicationList(medications: any) {
    if (!medications) return;
    for (let item of medications) {
      this.addMedicationItem(item);
    }
  }

  private filterMedication(value: string): Observable<any[]> {
    this.searchCompleted = false;
    return this.medicationService.getMedicationsBySearchTerm(value)
      .pipe(tap(() => this.searchCompleted = true));
  }

  /* private filterPatients(value: string): Observable<any[]> {
    if (!value || value === '' || typeof value !== 'string') {
      return of([...this.patients!]);
    } else {
      const filterTerms = value.toLowerCase().split(' ');
      const filtered = this.patients!.filter(patient => {
  
        const icdCodesMatch = patient.icdCodes?.some((icdCode: any) =>
          filterTerms.every(term =>
            _.includes((icdCode.code || '').toLowerCase(), term) ||
            _.includes((icdCode.description || '').toLowerCase(), term)
          )
        );
  
        const patientMatch = filterTerms.every(term =>
          _.includes(patient.name.toLowerCase(), term) ||
          _.includes(patient.surname.toLowerCase(), term) ||
          _.includes(patient.patientNumber?.toLowerCase(), term) ||
          _.includes(patient.diagnosis?.toLowerCase(), term)
        );
  
        return icdCodesMatch || patientMatch;
      });
  
      const sorted = _.orderBy(filtered, ['surname', 'name'], ['asc', 'asc']);
      return of(sorted)
    }
  } */


  findSimilarMeds(medication: any) {

    const sub = this.medicationService.getSimilarMedicatsions(medication.nappiCode)
      .subscribe(result => {
        const index = this.medicationList.findIndex(item => item.id === medication.id);
        this.medicationList[index].relatedProducts = result;
        this.cd.detectChanges();
        sub.unsubscribe();
      })

  }

  getIconName(dosageForm: string): string {
    return MEDICATION_ICONS[dosageForm] || '';
  }

  getRouteOptions(med: any) {

    const topRoutes: { [key: string]: string[] } = {
      vial: ['intravenous', 'intramuscular', 'subcutaneous', 'intradermal', 'inhalation'],
      injection: ['intravenous', 'intramuscular', 'subcutaneous', 'intradermal', 'inhalation'],
      capsule: ['oral', 'sublingual', 'buccal', 'rectal', 'inhalation'],
      tablet: ['oral', 'sublingual', 'buccal', 'rectal', 'inhalation'],
      suspension: ['oral', 'sublingual', 'buccal', 'rectal', 'inhalation'],
      liquid: ['oral', 'sublingual', 'buccal', 'rectal', 'inhalation'],
      syrup: ['oral', 'sublingual', 'buccal', 'rectal', 'inhalation'],
      solution: ['oral', 'sublingual', 'buccal', 'rectal', 'inhalation'],
      effervescent_tablet: ['oral', 'sublingual', 'buccal', 'rectal', 'inhalation'],
      suppository: ['rectal'],
      drops: ['oral', 'optic', 'otic', 'nasal'],
      inhaler: ['inhalation'],
      nebuliser: ['inhalation'],
      ointment: ['topical', 'transdermal', 'rectal', 'vaginal', 'buccal', 'nasal'],
      lotion: ['topical', 'transdermal', 'rectal', 'vaginal', 'buccal', 'nasal'],
      cream: ['topical', 'transdermal', 'rectal', 'vaginal', 'buccal', 'nasal'],
      gel: ['topical', 'transdermal', 'rectal', 'vaginal', 'buccal', 'nasal'],
      vaginal_cream: ['topical', 'transdermal', 'vaginal', 'buccal'],
      vaginal_gel: ['topical', 'transdermal', 'vaginal', 'buccal'],
      infusion: ['intravenous'],
    };

    let routeOptions = ROUTE_OPTIONS;
    if (topRoutes[med.dosageForm]) {
      const topRouteValues = topRoutes[med.dosageForm];
      routeOptions = _.filter(ROUTE_OPTIONS, option => _.includes(topRouteValues, option.value) || !_.includes(topRouteValues, option.value));
      routeOptions = _.sortBy(routeOptions, option => {
        const index = _.indexOf(topRouteValues, option.value);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      });
    }

    return routeOptions;
  }


  getUnitOptions(med: any) {
    let unitOptions: string[] = [];
    switch (med.dosageForm) {
      case 'vial':
        unitOptions = this.getUnitOptionsByIngredient(med);
        break;
      case 'injection':
        unitOptions = this.getUnitOptionsByIngredient(med);
        break;
      case 'capsule':
        // [capsule if > 1 ingredient else ingredient unit]
        if (med.ingredients?.length > 1) {
          unitOptions = ['capsule/s'];
        } else {
          unitOptions = this.getUnitOptionsByIngredient(med);
        }
        break;
      case 'tablet':
      case 'effervescent tablet':
        // [tablet if > 1 ingredient else ingredient unit]
        if (med.ingredients?.length > 1) {
          unitOptions = ['tablet/s'];
        } else {
          unitOptions = this.getUnitOptionsByIngredient(med);
        }
        break;
      case 'suppository':
        unitOptions = ['suppository'];
        break;
      case 'suspension':
        unitOptions = ['mg'];
        break;
      case 'liquid':
        unitOptions = ['ml, mg'];
        break;
      case 'syrup':
        unitOptions = ['ml'];
        break;
      case 'solution':
        unitOptions = [];
        break;
      case 'drops':
        unitOptions = ['drop/s'];
        break;
      case 'inhaler':
        unitOptions = ['puff/s'];
        break;
      case 'ointment':
      case 'lotion':
      case 'cream':
      case 'vaginal cream':
      case 'vaginal gel':
      case 'gel':
      case 'eye ointment':
        unitOptions = ['application'];
        break;
      case 'infusion':
        unitOptions = this.getUnitOptionsByIngredient(med);
        break;
      case 'nebuliser':
        unitOptions = ['ampule/s'];
        break;
      case 'chewing gum':
      case 'vacutainer':
      case 'powder':
      case 'sachet':
      case 'penset':
      case 'kit':
      case 'stent':
        unitOptions = ['unit/s'];
        break;
      case 'patch':
        unitOptions = ['patch/es'];
        break;
      case 'spray':
        unitOptions = ['spray/s'];
        break;
      default:
        unitOptions = [];
        break;
    }
    return unitOptions;
  }

  private getUnitOptionsByIngredient(med: any): string[] {
    // Check if the ingredients array exists and has at least one item
    if (!med.ingredients || med.ingredients.length === 0) {
      return [];
    }

    const unit = med.ingredients[0].unit;

    if (!unit) {
      return [];
    }

    if (unit.indexOf('/') === -1) {
      return [unit];
    } else {
      const split = unit.split('/');

      for (let i of split) {
        if (i.trim()) {
          return [i.trim()];
        }
      }
    }

    return [];
  }


  resetInput(): void {
    setTimeout(() => {
      this.inputElement.nativeElement.value = '';
      this.medicationControl.reset();
      this.searchCompleted = false;
    });
  }

  resetState(): void {
    setTimeout(() => {
      this.resetInput();
      this.medicationList = [];
    });
  }

  private getDefaultRoute(med: any) {
    let route = '';
    switch (med.dosageForm) {
      case 'capsule':
      case 'tablet':
      case 'suspension':
      case 'liquid':
      case 'syrup':
      case 'solution':
      case 'effervescent tablet':
        route = 'Oral';
        break;
      case "suppository":
        route = 'Rectal';
        break;
      case 'inhaler':
      case 'nebuliser':
        route = 'Inhalation';
        break;
      case 'infusion':
        route = 'Intravenous';
        break;
      default:
        route = '';
        break;
    }
    return route;
  }

  async addMedicationItem(item: any): Promise<void> {
    const defaultUnits = this.getUnitOptions(item);
    const defaultRoutes = this.getRouteOptions(item);

    const newItem = {
      dose: '',
      unit: item.unit ? item.unit : defaultUnits[0],
      frequency: '',
      route: item.route ? item.route : this.getDefaultRoute(item),
      duration: '',
      repeat: 0,
      unitOptions: defaultUnits, // Store computed unit options
      routeOptions: defaultRoutes, // Store computed route options
      frequencyControl: new FormControl(''),
      routeControl: new FormControl(''),
      durationControl: new FormControl(''),
      instructionControl: new FormControl(''),
      filteredFrequencyOptions: new Observable<string[]>(),
      filteredRouteOptions: new Observable<string[]>(),
      filteredDurationOptions: new Observable<string[]>(),
      filteredInstructions: new Observable<string[]>(),
      ...item
    };

    // Initialize form controls and filtered options
    newItem.frequencyControl.setValue(newItem.frequency);
    newItem.routeControl.setValue(newItem.route);
    newItem.durationControl.setValue(newItem.duration);
    newItem.instructionControl.setValue(newItem.instructions);

    newItem.filteredFrequencyOptions = newItem.frequencyControl.valueChanges.pipe(
      startWith(''),
      map((value: any) => this._filterOptions(value, this.frequencyOptions))
    );

    newItem.filteredRouteOptions = newItem.routeControl.valueChanges.pipe(
      startWith(''),
      map((value: any) => this._filterOptions(value, this.getRouteOptions(newItem)))
    );

    newItem.filteredDurationOptions = newItem.durationControl.valueChanges.pipe(
      startWith(''),
      map((value: any) => this._filterOptions(value, this.durationOptions))
    );

    // Setup the initial filtered instructions observable
    newItem.filteredInstructions = newItem.instructionControl.valueChanges.pipe(
      startWith(''),
      map((value: any) => {
        const instructionsList = newItem.frequencyControl.value?.includes('PRN')
          ? this.prnInstructions
          : this.instructionSuggestions;
        return this._filterOptions(value, instructionsList);
      })
    );

    // Subscribe to routeControl changes to update the instructions list
    newItem.frequencyControl.valueChanges.subscribe((frequencyValue: any) => {
      newItem.filteredInstructions = newItem.instructionControl.valueChanges.pipe(
        startWith(''),
        map((value: any) => {
          const instructionsList = frequencyValue.value.includes('PRN')
            ? this.prnInstructions
            : this.instructionSuggestions;
          return this._filterOptions(value, instructionsList);
        })
      );
    });

    this.medicationList.unshift(newItem);
    this.existingData.add(item);
    this.changed.emit(this.medicationList);
    this.resetInput();
  }


  async addManualMedication(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const name = this.medicationControl.value;
    const newItem = {
      name,
      dose: '',
      unit: '',
      frequency: '',
      route: '',
      duration: '',
      repeat: 0,
      unitOptions: '',
      routeOptions: '',
      frequencyControl: new FormControl(''),
      routeControl: new FormControl(''),
      durationControl: new FormControl(''),
      instructionControl: new FormControl(''),
      filteredFrequencyOptions: new Observable<string[]>(),
      filteredRouteOptions: new Observable<string[]>(),
      filteredDurationOptions: new Observable<string[]>(),
      filteredInstructions: new Observable<string[]>(),
      isManual: true,
    };

    this.medicationList.unshift(newItem);
    this.changed.emit(this.medicationList);
    this.resetInput();
  }


  private _filterOptions(value: string, options: any[]): any[] {
    if (typeof value !== 'string') {
      return options;
    }
    const filterValue = value.toLowerCase();
    return options.filter(option => option.label ? option.label.toLowerCase().includes(filterValue) : option.toLowerCase().includes(filterValue));
  }


  displayOption(option: any): string {
    if (typeof option === 'string') {
      return option;
    }
    return option ? option.label : '';
  }


  displayPatient(patient: any): string {
    return patient ? `${patient.title ? patient.title + ' ' : ''}${patient.name} ${patient.surname}` : '';
  }

  /* setSelectedPatient(item: any) {
    this.selectedPatient = item;
  } */


  async deleteMedication(event: MouseEvent, index: number) {
    event.preventDefault();
    event.stopPropagation();
    const confirmed = await this.feedbackService.showConfirmation();
    if (!confirmed) return;
    this.medicationList.splice(index, 1);
  }

  private openSigningAuthDialog() {
    return new Promise<void>((resolve, reject) => {
      const dialogRef = this.dialog.open(this.authorizeSigningDialog!, { width: '300px', disableClose: true });
      const sub = dialogRef.afterClosed().subscribe((authResult: any[]) => {
        if (!authResult) {
          reject('User cancelled signing auth dialog');
        } else {
          resolve();
        }
        sub.unsubscribe();
      });
    });
  }

  /* private async checkSigningRequirements(prescription: any): Promise<boolean> {
    try {
      const user = this.userService.user;
      if (prescription.filter((med: any) => med.schedule === 'S5' || med.schedule === 'S6').length > 0) {
        if (user.userType === 'doctor') {
          await this.openSigningAuthDialog();
          try {
            const result = await this.medicationService.verifySigningDoctor({ email: user.email, password: this.signingAuthPassword }).toPromise();
            if (result.success) {
              return true
            } else {
              if (result.message.includes('Invalid email or password')) {
                this.feedbackService.showToast('Authorization failed: Invalid password. An unsigned prescription requiring manual signing will be generated now.');
              } else if (result.message.includes('User type is invalid')) {
                this.feedbackService.showToast('Authorization failed: Only physicians are authorized to sign prescriptions. An unsigned prescription requiring manual signing will be generated now.');
              } else {
                this.feedbackService.showToast('Authorization failed. An unsigned prescription requiring manual signing will be generated now.');
              }
              return false;
            }
          } catch (error: any) {
            return false;
          }
        } else {
          await this.feedbackService.showToast('You are not authorized to generate a signed prescription. An unsigned prescription requiring manual signing will be generated now.');
          return false;
        }
      } else {
        return true;
      }
    } catch (error: any) {
      this.feedbackService.showToast('Authorization failed. An unsigned prescription requiring manual signing will be generated now.');
      return false;
    }
  } */

  private async checkSigningRequirements(prescription: any): Promise<boolean> {
    try {
      const user = this.userService.user;
      this.forceSignature = prescription.some((med: any) => med.schedule === 'S5' || med.schedule === 'S6');

      if (user.userType !== 'doctor' || this.forceSignature) {
        await this.openSigningAuthDialog();

        try {
          const result = await this.medicationService.verifySigningDoctor({
            id: this.signingAuthPhysician,
            password: this.signingAuthPassword
          }).toPromise();

          if (result.success) {
            return true;
          } else {
            this.handleAuthorizationFailure(result.message);
            return false;
          }
        } catch (error: any) {
          this.feedbackService.showToast('Authorization failed. An unsigned prescription requiring manual signing will be generated now.');
          return false;
        }
      }

      return true;
    } catch (error: any) {
      //this.feedbackService.showToast('Authorization failed. An unsigned prescription requiring manual signing will be generated now.');
      return false;
    }
  }


  private async checkMedicationInteractions(details: any): Promise<boolean> {
    try {
      const user = this.userService.user;
      const group = this.groupService.group;
      const patient = details.patient;
      const medication = details.prescription;

      const check = await this.medicationService.checkIndications(medication, patient.allergies, patient.medicalHistory, user.id, group.id).toPromise();
      if (check.success && check.result.indications?.length) {
        const indications = check.result.indications;
        const message = indications.map((ind: any) => `- ${ind.description}`).join('\n')
        const confirmed = await this.feedbackService.showConfirmation('WARNING', message, [], 'Cancel', 'Continue', true)
        return confirmed;
      } else {
        return true;
      }
    } catch (error: any) {
      //this.feedbackService.showToast('Authorization failed. An unsigned prescription requiring manual signing will be generated now.');
      return true;
    }
  }

  private handleAuthorizationFailure(message: string): void {
    if (message.includes('Invalid email or password')) {
      this.feedbackService.showToast('Authorization failed: Invalid password. An unsigned prescription requiring manual signing will be generated now.');
    } else if (message.includes('User type is invalid')) {
      this.feedbackService.showToast('Authorization failed: Only physicians are authorized to sign prescriptions. An unsigned prescription requiring manual signing will be generated now.');
    } else {
      this.feedbackService.showToast('Authorization failed. An unsigned prescription requiring manual signing will be generated now.');
    }
  }

  compileDetails() {

    /* const patient = {
      id: '123',
      title: 'Mr',
      name: 'John',
      surname: 'Smith',
      dob: '01/05/1964',
      gender: 'Male',
      medicalAid: 'Discovery',
      medicalAidNumber: '456123',
      medicalAidPlan: 'Classic Comprehensive',
      medicalDependantCode: '01',
      diagnosis: 'Frontal Lobe Meningioma',
      idNumber: '987654321',
      passportNumber: '',
      email: 'email@patient.com',
      mobileNumber: '+27 82346798',
      address: '123 Street, Some Suburb, City',
      icdCodes: [{ code: 'D32' }]
    }; */

    const prescription = this.getMedicationList();
    return { patient: this.patient, prescription }
  }

  getMedicationList() {
    return this.medicationList.map(med => {
      return {
        id: med.id,
        name: med.name,
        dosageForm: med.dosageForm,
        nappiCode: med.nappiCode,
        packSize: med.packSize,
        medication: med.name,
        maxPrice: med.maxPrice,
        isGeneric: med.isGeneric,
        ingredients: med.ingredients,
        dose: med.dose,
        unit: med.unit,
        frequency: med.frequencyControl.value.label || med.frequencyControl.value,
        route: med.routeControl.value.label || med.routeControl.value,
        duration: med.durationControl.value.label || med.durationControl.value,
        repeat: med.repeat,
        instructions: med.instructionControl.value,
        schedule: med.schedule,
      }
    });
  }

  isValid(): boolean {
    return this.medicationList.length > 0;
  }

  async downloadPrescription(): Promise<any> {
    const details = this.compileDetails();
    const checkIndications = await this.checkMedicationInteractions(details);
    if (!checkIndications) return false;

    const allowSigning = await this.checkSigningRequirements(details.prescription);
    const patientName = `${details.patient.name}_${details.patient.surname}`;

    try {
      const result = await this.medicationService.createPatientPrescriptionPdf(
        details.patient,
        details.prescription,
        allowSigning,
        this.confirmSubstitutions,
        this.signingAuthPhysician,
        `${patientName}_${moment().valueOf()}_prescription.pdf`
      );
      return result;
    } catch (error) {
      console.error('Error downloading prescription:', error);
      return false;
    }
  }

  async emailPrescription(email: string, ccEmail: string, triggerDownload: boolean = false, includeSignature?: boolean): Promise<boolean> {
    const details = this.compileDetails();
    const authResult = await this.checkSigningRequirements(details.prescription);
    // If includeSignature is explicitly provided, respect it.
    // - When true: include signature only if authorization passes
    // - When false: force no signature
    // - When undefined: keep previous behavior (include if authorized by default rules)
    const allowSigning = includeSignature === undefined
      ? authResult
      : (includeSignature ? authResult : false);
    const patientName = `${details.patient.name}_${details.patient.surname}`;

    return new Promise<boolean>((resolve) => {
      const sub = this.medicationService.emailPrescription(
        details.patient,
        this.groupService.group.id,
        details.prescription,
        allowSigning,
        this.confirmSubstitutions,
        this.signingAuthPhysician,
        `${patientName}_${moment().valueOf()}_prescription.pdf`,
        email,
        ccEmail,
        triggerDownload
      ).subscribe(
        result => { sub.unsubscribe(); resolve(true); },
        error => {
          console.error('Error emailing prescription:', error);
          sub.unsubscribe();
          resolve(false);

        }
      );
    });
  }

}
